import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

/**
 * SnowflakeSignupCard Component
 *
 * A prominent card-style button that encourages users to sign up for a free Snowflake account
 * if they don't already have one. Links to Snowflake's trial signup page.
 *
 * Features:
 * - Large, card-style design for visibility
 * - DataOps theme colors
 * - Hover animations
 * - External link indicator
 * - Accessible keyboard navigation
 *
 * @component
 * @example
 * ```tsx
 * <SnowflakeSignupCard />
 * ```
 */
const SnowflakeSignupCard: React.FC = () => {
  const SNOWFLAKE_TRIAL_URL = 'https://signup.snowflake.com/';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
      className="my-6"
    >
      <a
        href={SNOWFLAKE_TRIAL_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block group"
        aria-label="Sign up for a free Snowflake trial account"
      >
        <div className="rounded-lg border-2 border-dataops-primary-blue/20 bg-gradient-to-br from-dataops-primary-blue/5 to-dataops-secondary-blue/5 p-6 transition-all duration-300 hover:border-dataops-primary-blue/40 hover:shadow-lg hover:scale-[1.02]">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className="mb-2">
                <h3 className="text-lg text-dataops-dark">Don&apos;t have a Snowflake account?</h3>
              </div>
              <p className="text-sm text-secondary-text">
                Get started with a free Snowflake trial account. No credit card required.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <span className="inline-flex items-center gap-2 text-sm text-dataops-primary-blue group-hover:text-hover-primary-blue transition-colors">
              Sign up for free trial
              <ArrowRightIcon
                className="h-4 w-4 group-hover:translate-x-1 transition-transform duration-200"
                aria-hidden="true"
              />
            </span>
          </div>
        </div>
      </a>
    </motion.div>
  );
};

export default SnowflakeSignupCard;
